import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getProfileReactions, type ProfileReactionRow } from '../../api/reactionsReader'

type FilterType = 'all' | 'like' | 'comment'
const PAGE_SIZE = 25

function formatTimestamp(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

export default function ReactionsListPage() {
  const { handle } = useParams()
  const navigate = useNavigate()

  const [rows, setRows] = useState<ProfileReactionRow[]>([])
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canLoadMore = rows.length < count

  useEffect(() => {
    if (!handle) return
    setRows([])
    setPage(1)
    setCount(0)
    setError(null)
    void loadPage(1, true, query, filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, filter])

  const loadPage = async (nextPage: number, replace = false, q = query, type = filter) => {
    if (!handle) return
    setLoading(true)
    setError(null)
    try {
      const data = await getProfileReactions(handle, {
        page: nextPage,
        page_size: PAGE_SIZE,
        q,
        type,
      })
      setCount(data.count || 0)
      setRows((prev) => (replace ? data.results : [...prev, ...data.results]))
      setPage(nextPage)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load reactions')
    } finally {
      setLoading(false)
    }
  }

  const onSearchSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await loadPage(1, true, query, filter)
  }

  if (!handle) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 text-white">
        <div className="text-lg font-semibold">Profile not found.</div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 text-white">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-lg border border-white/10 px-3 py-1.5 text-sm hover:bg-white/5">
          Back
        </button>
        <div className="text-xl font-extrabold">Reactions</div>
        <div className="text-sm text-white/60">@{handle}</div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <button
          className={`rounded-full px-3 py-1.5 text-sm ${filter === 'all' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={`rounded-full px-3 py-1.5 text-sm ${filter === 'like' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`}
          onClick={() => setFilter('like')}
        >
          Likes
        </button>
        <button
          className={`rounded-full px-3 py-1.5 text-sm ${filter === 'comment' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`}
          onClick={() => setFilter('comment')}
        >
          Comments
        </button>
      </div>

      <form className="mb-6" onSubmit={onSearchSubmit}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search actor"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30"
        />
      </form>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl border border-white/10 bg-white/5 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-8 text-center text-white/70">
          No reactions found.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row, index) => {
          const actorLabel = row.actor.display_name || row.actor.handle || 'Unknown'
          const handleLabel = row.actor.handle ? `@${row.actor.handle}` : ''
          const postHref = row.post?.id ? `/p/${row.post.id}` : ''
          return (
            <article key={`${row.type}-${row.created_at}-${index}`} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="flex items-start gap-3">
                {row.actor.avatar_url ? (
                  <img src={row.actor.avatar_url} className="h-11 w-11 rounded-full object-cover" alt={actorLabel} />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-white/10" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-white truncate">{actorLabel}</div>
                    <div className="text-xs text-white/65 truncate">{handleLabel}</div>
                  </div>
                  <div className="mt-1 text-sm text-white/80">
                    {row.type === 'like' ? (
                      <span><i className="fa-solid fa-heart mr-1 text-rose-400" aria-hidden="true" />Liked your post</span>
                    ) : (
                      <span><i className="fa-regular fa-comment mr-1 text-sky-300" aria-hidden="true" />Commented on your post</span>
                    )}
                    {row.post ? (
                      <span className="text-white/60">
                        {' '}•{' '}
                        {postHref ? (
                          <Link className="hover:underline" to={postHref}>
                            {row.post.title || 'Post'}
                          </Link>
                        ) : (
                          row.post.title || 'Post'
                        )}
                      </span>
                    ) : null}
                  </div>
                  {row.type === 'comment' && row.comment?.snippet ? (
                    <div className="mt-1 text-xs text-white/65 line-clamp-2">"{row.comment.snippet}"</div>
                  ) : null}
                </div>
                <div className="text-xs text-white/60 whitespace-nowrap">{formatTimestamp(row.created_at)}</div>
              </div>
            </article>
          )
        })}
      </div>

      {canLoadMore && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => void loadPage(page + 1)}
            disabled={loading}
            className="rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-60"
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}
